import { useState } from 'react'
import type { JSX } from 'react'
import { Dialog, VisuallyHidden, useMediaQuery } from '@buffer-mono/popcorn'
import { useDispatch, useSelector } from 'react-redux'
import { useSplitEnabled } from '@buffer-mono/features'

import type { OrchestratorRootState } from '../../../../../common/events/types'
import { useUser } from '../../../../../common/context/User'
import { trackChannelConnectionAborted } from '../../tracking'
import { resetChannelConnectionsState } from '../../../store/channelConnectionsReducer'
import ChannelStoreFront from '../ChannelStoreFront/ChannelStoreFront'
import BlueskyAuth from '../BlueskyAuth/BlueskyAuth'
import { Service } from '../../types'
import type { OnRedirectParams } from '../../types'
import InstagramSelectAccountType from '../InstagramSelectAccountType/InstagramSelectAccountType'
import { Mastodon } from '../services/Mastodon/Mastodon'
import { Facebook } from '../services/Facebook/Facebook'
import { WhatsAppPreConnection } from '../services/WhatsApp/WhatsAppPreConnection'
import type { WhatsAppCloseMode } from '../services/WhatsApp/WhatsAppPreConnection'
import { reloadPage } from '../../../../../common/utils/urls'

import styles from './ChannelConnectionsDialog.module.css'
import { ExitChannelConnectionDialog } from '../ExitChannelConnectionDialog/ExitChannelConnectionDialog'

interface ChannelConnectionsDialogProps {
  onRedirect: (args: OnRedirectParams) => void
}

function ChannelConnectionsDialog({
  onRedirect,
}: ChannelConnectionsDialogProps): JSX.Element | null {
  const user = useUser()
  const { isEnabled: whatsappEnabled } = useSplitEnabled(
    'whatsapp-channel-connection',
    { splitKey: user.currentOrganization?.id },
  )
  const {
    selectedService,
    name,
    selectedRefreshChannelId,
    isRefreshingChannelConnection,
    showChannelStorefront,
  } = useSelector((state: OrchestratorRootState) => state.channelConnections)

  const dispatch = useDispatch()
  const whatsAppChannelId = isRefreshingChannelConnection
    ? selectedRefreshChannelId
    : undefined

  function cancelConnection(): void {
    if (selectedService) {
      trackChannelConnectionAborted({
        account: user,
        service: selectedService,
      })
    }
    dispatch(resetChannelConnectionsState({}))
  }

  const showBlueskyAuth = selectedService === Service.bluesky
  const showMastodonAuth = selectedService === Service.mastodon
  const showFacebookAuth = selectedService === Service.facebook
  const showWhatsAppPreConnection =
    selectedService === Service.whatsapp &&
    Boolean(user.currentOrganization?.id) &&
    whatsappEnabled &&
    !showChannelStorefront

  const showNewInstagramAuth =
    (selectedService === Service.instagram ||
      selectedService === Service.instagram_login_api) &&
    !showChannelStorefront

  const showPopcornDialog =
    showNewInstagramAuth ||
    showWhatsAppPreConnection ||
    selectedService === Service.bluesky ||
    selectedService === Service.mastodon ||
    selectedService === Service.facebook

  const shouldUsePopcornDialog = showPopcornDialog || showChannelStorefront
  const shouldShowChannelStoreFront = showChannelStorefront

  const isMobile = useMediaQuery('(width < 641px)')
  const dialogSize = isMobile ? 'maximize' : 'xlarge'

  const [showExitChannelConnectionDialog, setShowExitChannelConnectionDialog] =
    useState(false)
  const [isFacebookConnectionInProgress, setIsFacebookConnectionInProgress] =
    useState(false)
  const [whatsAppCloseMode, setWhatsAppCloseMode] =
    useState<WhatsAppCloseMode>('close')

  const canCloseWithoutWarning =
    shouldShowChannelStoreFront ||
    showWhatsAppPreConnection ||
    (showFacebookAuth && !isFacebookConnectionInProgress)

  function onOpenChange(): void {
    if (showWhatsAppPreConnection && whatsAppCloseMode === 'blocked') return
    if (showWhatsAppPreConnection && whatsAppCloseMode === 'reload') {
      reloadPage()
      return
    }
    if (canCloseWithoutWarning) cancelConnection()
    else setShowExitChannelConnectionDialog(true)
  }

  return (
    <Dialog open={shouldUsePopcornDialog} onOpenChange={onOpenChange}>
      <Dialog.Content
        className={styles.dialog}
        size={dialogSize}
        data-context={
          shouldShowChannelStoreFront ? 'channel-storefront' : undefined
        }
      >
        <VisuallyHidden>
          <Dialog.Title>Connect a channel</Dialog.Title>
          <Dialog.Description>
            Connect a social media channel to Buffer.
          </Dialog.Description>
        </VisuallyHidden>
        {shouldShowChannelStoreFront && <ChannelStoreFront />}
        {showWhatsAppPreConnection && user.currentOrganization?.id && (
          <WhatsAppPreConnection
            key={`${user.currentOrganization.id}:${whatsAppChannelId ?? ''}`}
            organizationId={user.currentOrganization.id}
            channelId={whatsAppChannelId}
            onCloseModeChange={setWhatsAppCloseMode}
          />
        )}
        {showExitChannelConnectionDialog && (
          <ExitChannelConnectionDialog
            setShowExitChannelConnectionDialog={
              setShowExitChannelConnectionDialog
            }
            onOpenChange={cancelConnection}
            selectedService={selectedService}
          />
        )}
        {showNewInstagramAuth && (
          <InstagramSelectAccountType onRedirect={onRedirect} />
        )}
        {showBlueskyAuth && (
          <BlueskyAuth
            onClose={cancelConnection}
            isRefreshingChannelConnection={isRefreshingChannelConnection}
            name={name}
            channelId={selectedRefreshChannelId}
          />
        )}
        {showMastodonAuth && (
          <Mastodon onRedirect={onRedirect} onClose={cancelConnection} />
        )}
        {showFacebookAuth && (
          <Facebook
            onRedirect={onRedirect}
            onConnectionInProgressChange={setIsFacebookConnectionInProgress}
          />
        )}
      </Dialog.Content>
    </Dialog>
  )
}

export default ChannelConnectionsDialog
